import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  CreateUserTodoResponse,
  TodoListMember,
  UserTodoItem,
} from '@dreamai/types';
import api from '../../../api';
import TaskComposerSheet from '../../common/TaskComposerSheet';
import type {
  TaskComposerAssignee,
  TaskComposerValues,
} from '../../common/TaskComposerSheet';
import { AWAITING_JOIN_LABEL } from './AssigneePicker';

interface MobileNewTaskSheetProps {
  open: boolean;
  onClose: () => void;
  agentId?: string;
  listId?: string;
  listName?: string;
  listMembers?: TodoListMember[];
  currentUserId: string;
  onTodoCreated: (todo: UserTodoItem) => void;
  onTodoConfirmed: (optimisticId: string, realId: string) => void;
  onTodoRejected: (optimisticId: string, message: string) => void;
}

const LABELS = {
  title: 'New task',
  description: 'Task',
  descriptionPlaceholder: 'Describe your task',
  dueDate: 'Add due date',
  assign: 'Assign',
  unassigned: 'Unassigned',
  awaitingGroup: 'Invited · assigned when they join',
  priority: 'Add priority',
  noPriority: 'No priority',
  priorities: {
    high: 'High priority',
    medium: 'Medium priority',
    low: 'Low priority',
  },
  submit: 'Create task',
  submitting: 'Creating…',
};

/**
 * Authenticated mobile "New" sheet for Reminders. Creates a personal task or
 * one in the selected shared list; the task is shown optimistically and
 * reconciled (or removed with a notice) once the API responds.
 */
const MobileNewTaskSheet: React.FC<MobileNewTaskSheetProps> = ({
  open,
  onClose,
  agentId,
  listId,
  listName,
  listMembers,
  currentUserId,
  onTodoCreated,
  onTodoConfirmed,
  onTodoRejected,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const assignees: TaskComposerAssignee[] = listId
    ? (listMembers ?? [])
        .filter((m) => m.status === 'accepted' || m.status === 'pending')
        .map((m) => ({
          userId: m.userId,
          name: m.name,
          label:
            m.userId === currentUserId
              ? `${m.name} (you)`
              : m.status === 'pending'
                ? `${m.name} · ${AWAITING_JOIN_LABEL}`
                : m.name,
          awaiting: m.status === 'pending',
        }))
    : [];

  const submit = async (values: TaskComposerValues) => {
    const assignee = values.assigneeUserId
      ? listMembers?.find((m) => m.userId === values.assigneeUserId)
      : undefined;
    const now = new Date().toISOString();
    const optimisticTodo: UserTodoItem = {
      id: `optimistic-${Date.now()}`,
      description: values.description,
      status: 'pending',
      assigneeType: 'user',
      dueDate: values.dueDate,
      priority: values.priority,
      agentId,
      listId,
      listName,
      assigneeUserId: assignee?.userId,
      assigneeName: assignee?.name,
      assigneeStatus: assignee?.status,
      createdByUserId: currentUserId,
      createdAt: now,
      updatedAt: now,
    };

    onTodoCreated(optimisticTodo);

    void (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<CreateUserTodoResponse>(
          '/user-todos',
          {
            description: values.description,
            assigneeType: 'user',
            dueDate: values.dueDate,
            priority: values.priority,
            agentId,
            ...(listId ? { listId } : {}),
            ...(assignee ? { assigneeUserId: assignee.userId } : {}),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.todo?.id) {
          onTodoConfirmed(optimisticTodo.id, response.data.todo.id);
        }
      } catch (err) {
        const maybe = err as { response?: { data?: { error?: string } } };
        onTodoRejected(
          optimisticTodo.id,
          maybe?.response?.data?.error || 'Could not add this task.',
        );
      }
    })();
  };

  return (
    <TaskComposerSheet
      open={open}
      onClose={onClose}
      onSubmit={submit}
      labels={LABELS}
      assignees={assignees}
      descriptionRows={2}
      className='reminders-new-task-sheet'
      testIdPrefix='reminders-new-task'
    />
  );
};

export default MobileNewTaskSheet;
